import { useTranslation } from "react-i18next";

import { CircularProgress } from "@mui/material";

import { useState } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";

import { ButtonColor } from "@/components/Button/types";

import { Icon } from "@/components";

import {
	useCreateToDoMutation,
	useDeleteToDoMutation,
} from "@/pages/Private/redux/to-do/to-do.api";

import DeleteModal from "@/components/Modals/DeleteModal";

import { CreateToDoInfo, CreateToDoInfoSchema, ToDoInfo } from "../../ToDoList/schema/to-do";
import EditableToDoItem from "./EditableToDoItem";
import EditModeToDoItem from "./EditableToDoItem/EditModeToDoItem";
import { ShowingMode } from "../../ToDoList/enum/ShowingMode.enum";

interface EditableToDoItemListProps {
	data?: ToDoInfo[];
	isLoading: boolean;
	editingToDoId: number;
	setEditingToDoId: (value: number) => void;
}

export default function EditableToDoItemList({
	data,
	isLoading,
	editingToDoId,
	setEditingToDoId,
}: EditableToDoItemListProps) {
	const [isCreating, setCreating] = useState(false);
	const { t } = useTranslation();
	const ts = (key: string, options = {}) => t(`manage-to-dos.${key}`, options);
	const [deletingToDo, setDeletingToDo] = useState<ToDoInfo | null>(null);
	const [copyingToDo, setCopyingToDo] = useState<ToDoInfo | null>(null);
	const [createToDo, { isLoading: isCreatingToDo }] = useCreateToDoMutation();
	const [deleteToDo, { isLoading: isToDoDeleting }] = useDeleteToDoMutation();

	const handleCreateToDo = async (values: CreateToDoInfo) => {
		try {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const res: any = await createToDo(values);

			if (res?.data?.id) {
				setEditingToDoId(res?.data?.id);
			}
		} catch (error) {
			console.error(error);
		} finally {
			setCreating(false);
		}
	};

	const handleDeleteToDo = async () => {
		if (!deletingToDo?.id) {
			return;
		}

		try {
			await deleteToDo(deletingToDo?.id ?? 0).unwrap();
			setDeletingToDo(null);
		} catch (err) {
			console.error(err);
		}
	};

	const onEditToDo = (id: number) => {
		setEditingToDoId(id);
		setCreating(false);
	};

	const handleCopyToDo = (toDo: ToDoInfo | null) => {
		if (!toDo) {
			return;
		}

		const withoutId = {
			...toDo,
			id: undefined,
			showingMode: toDo.isOutOfDate ? ShowingMode.OUT_OF_DATE : ShowingMode.ONLY_NEW,
		};

		setCopyingToDo(withoutId);
		setCreating(true);
	};

	return (
		<>
			<div
				className={`w-full border border-primary-gray-lightest p-1 pt-4 rounded-16 space-y-4 bg-surface`}
			>
				<div className="flex justify-between items-center px-3">
					<h3 className="w-full flex gap-2 text-primary-blue-dark text-[14px] font-medium">
						{ts("subtitle")}

						<span className="border border-primary-gray-lightest bg-primary-gray-lightest rounded-[4px] w-5 h-5 flex items-center justify-center text-text text-[12px] font-medium">
							{data?.length ?? "0"}
						</span>
					</h3>
					<Button
						className="!min-w-5 !min-h-5 !max-w-5 !max-h-5 !p-0"
						color={ButtonColor.ACTION}
						image={<Icon className="w-3 h-4 text-white" icon={faPlus} />}
						title=""
						type="button"
						onClick={() => {
							setCreating(true);
							if (editingToDoId !== -1) {
								setEditingToDoId(-1);
							}
						}}
					/>
				</div>
				{isCreatingToDo || isLoading ? (
					<div className="w-full flex justify-center py-10 min-h-[calc(100vh-276px)] max-h-[calc(100vh-276px)]">
						<CircularProgress className="!w-12 !h-12" />
					</div>
				) : (
					<div className="space-y-1 min-h-[calc(100vh-276px)]">
						{isCreating && (
							<EditModeToDoItem
								data={copyingToDo ?? {}}
								isLoading={isCreatingToDo}
								setEditing={() => setCreating(false)}
								validationSchema={CreateToDoInfoSchema}
								onSubmit={handleCreateToDo}
							/>
						)}
						{data?.length ? (
							data?.map((item) => (
								<EditableToDoItem
									key={item.id}
									data={item}
									isEditing={editingToDoId === item?.id}
									setCopyingToDo={handleCopyToDo}
									setDeletingToDo={setDeletingToDo}
									setEditing={onEditToDo}
								/>
							))
						) : (
							<p className="text-[16px] text-subtle text-center py-4">{ts("list-empty")}</p>
						)}
					</div>
				)}
			</div>
			<DeleteModal
				description={ts("delete-description", { taskTitle: deletingToDo?.title })}
				handleClose={() => setDeletingToDo(null)}
				handleDelete={handleDeleteToDo}
				isLoading={isToDoDeleting}
				isShowing={!!deletingToDo}
				title={ts("delete-title")}
			/>
		</>
	);
}
